/**
 * Token Encryption Service
 * 
 * Provides secure encryption for OAuth tokens before storing in Firestore.
 * Uses Web Crypto API for client-side encryption with organization-specific keys.
 * 
 * SECURITY ARCHITECTURE:
 * - Each organization has a unique encryption key derived from their org ID
 * - Keys are derived using PBKDF2 with a secure salt
 * - Tokens are encrypted using AES-GCM (256-bit)
 * - IV (Initialization Vector) is unique per encryption and stored with ciphertext
 * 
 * NOTE: For production environments with higher security requirements,
 * consider moving encryption to Firebase Functions with Cloud KMS integration.
 */

// Salt for key derivation - should be stored securely in environment
const ENCRYPTION_SALT = import.meta.env.VITE_TOKEN_ENCRYPTION_SALT || 
  'aideline-oauth-token-salt-v1';

// Additional entropy from environment (optional but recommended)
const ENCRYPTION_PEPPER = import.meta.env.VITE_TOKEN_ENCRYPTION_PEPPER || '';

/**
 * Derives a cryptographic key from organization ID
 * Uses PBKDF2 with SHA-256
 */
async function deriveKey(orgId: string): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  
  // Combine orgId with pepper for additional security
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(orgId + ENCRYPTION_PEPPER),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode(ENCRYPTION_SALT),
      iterations: 100000,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypts a token string
 * Returns base64-encoded string containing IV + ciphertext
 */
export async function encryptToken(token: string, orgId: string): Promise<string> {
  try {
    const key = await deriveKey(orgId);
    const encoder = new TextEncoder();
    
    // Generate random IV (12 bytes for AES-GCM)
    const iv = crypto.getRandomValues(new Uint8Array(12));
    
    // Encrypt the token
    const ciphertext = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      encoder.encode(token)
    );

    // Combine IV + ciphertext
    const combined = new Uint8Array(iv.length + ciphertext.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(ciphertext), iv.length);

    // Return as base64
    return btoa(String.fromCharCode(...combined));
  } catch (error) {
    console.error('Token encryption failed:', error);
    throw new Error('Failed to encrypt token');
  }
}

/**
 * Decrypts an encrypted token
 * Expects base64-encoded string containing IV + ciphertext
 */
export async function decryptToken(encryptedToken: string, orgId: string): Promise<string> {
  try {
    const key = await deriveKey(orgId);
    
    // Decode base64
    const combined = Uint8Array.from(atob(encryptedToken), c => c.charCodeAt(0));
    
    // Extract IV (first 12 bytes) and ciphertext
    const iv = combined.slice(0, 12);
    const ciphertext = combined.slice(12);

    // Decrypt
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      ciphertext
    );

    return new TextDecoder().decode(decrypted);
  } catch (error) {
    console.error('Token decryption failed:', error);
    throw new Error('Failed to decrypt token');
  }
}

/**
 * Checks if a token appears to be encrypted
 * Encrypted tokens are base64-encoded and have a specific length pattern
 */
export function isTokenEncrypted(token: string): boolean {
  // Encrypted tokens are base64 and at least contain IV (12 bytes) + some ciphertext
  // Base64 of 12 bytes = 16 characters minimum
  if (!token || token.length < 24) return false;
  
  try {
    // Try to decode as base64
    const decoded = atob(token);
    // Valid base64 and reasonable length for encrypted content
    return decoded.length >= 12;
  } catch {
    // Not valid base64, so not encrypted
    return false;
  }
}

/**
 * Encrypts token data object containing access and refresh tokens
 */
export async function encryptTokens(
  orgId: string,
  tokens: { accessToken: string; refreshToken: string }
): Promise<{ encryptedAccessToken: string; encryptedRefreshToken: string }> {
  const [encryptedAccessToken, encryptedRefreshToken] = await Promise.all([
    encryptToken(tokens.accessToken, orgId),
    encryptToken(tokens.refreshToken, orgId)
  ]);

  return { encryptedAccessToken, encryptedRefreshToken };
}

/**
 * Decrypts token data object
 */
export async function decryptTokens(
  orgId: string,
  encryptedTokens: { encryptedAccessToken: string; encryptedRefreshToken: string }
): Promise<{ accessToken: string; refreshToken: string }> {
  const [accessToken, refreshToken] = await Promise.all([
    decryptToken(encryptedTokens.encryptedAccessToken, orgId),
    decryptToken(encryptedTokens.encryptedRefreshToken, orgId)
  ]);

  return { accessToken, refreshToken };
}

